import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaUpload, 
    FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaExchangeAlt, 
    FaFolder, FaSave, FaCog
} from 'react-icons/fa';
import './AddGallery.css';

// --- IMPORT AXIOS INSTANCE ---
import axiosInstance from '../../api/axiosInstance';

// Ensure this matches your backend port for IMAGE display
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

// --- FOLDER TOOLS MODAL ---
const FolderToolsModal = ({ isOpen, onClose, folders, onRename, onDelete }) => {
    const [activeTab, setActiveTab] = useState('rename');
    const [selectedFolder, setSelectedFolder] = useState('');
    const [newName, setNewName] = useState('');
    const [deleteType, setDeleteType] = useState('content');

    useEffect(() => {
        if (!isOpen) {
            setSelectedFolder('');
            setNewName('');
            setDeleteType('content');
            setActiveTab('rename');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleExecute = () => {
        if (!selectedFolder) return Swal.fire('Error', 'Please select a folder.', 'error');
        if (activeTab === 'rename') {
            if (!newName) return Swal.fire('Error', 'Please enter a new name.', 'error');
            onRename(selectedFolder, newName);
        } else {
            onDelete(selectedFolder, deleteType);
        }
    };

    return (
        <div className="add-galley-of-sk-college-modal-overlay">
            <div className="add-galley-of-sk-college-modal-content tools-modal" onClick={e => e.stopPropagation()}>
                <div className="add-galley-of-sk-college-modal-header">
                    <h2>Edit / Delete Folder</h2>
                    <button className="add-galley-of-sk-college-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="add-galley-of-sk-college-tabs">
                    <button className={`add-galley-of-sk-college-tab-btn ${activeTab === 'rename' ? 'active' : ''}`} onClick={() => setActiveTab('rename')}><FaEdit /> Rename Folder</button>
                    <button className={`add-galley-of-sk-college-tab-btn ${activeTab === 'delete' ? 'active' : ''}`} onClick={() => setActiveTab('delete')}><FaTrashAlt /> Delete Folder</button>
                </div>
                <div className="add-galley-of-sk-college-modal-body">
                    <div className="add-galley-of-sk-college-form-group">
                        <label>Select Folder</label>
                        <select className="add-galley-of-sk-college-select-input" value={selectedFolder} onChange={(e) => setSelectedFolder(e.target.value)}>
                            <option value="">-- Choose Folder --</option>
                            {folders.filter(f => f).map((f, i) => <option key={i} value={f}>{f}</option>)}
                        </select>
                    </div>
                    {activeTab === 'rename' && (
                        <div className="add-galley-of-sk-college-tab-content">
                            <div className="add-galley-of-sk-college-form-group">
                                <label>New Folder Name</label>
                                <input type="text" className="add-galley-of-sk-college-input" placeholder="Enter new name" value={newName} onChange={(e) => setNewName(e.target.value)} />
                            </div>
                        </div>
                    )}
                    {activeTab === 'delete' && (
                        <div className="add-galley-of-sk-college-tab-content">
                            <label className="add-galley-of-sk-college-radio-label">
                                <input type="radio" name="delType" checked={deleteType === 'folder'} onChange={() => setDeleteType('folder')} />
                                <div className="radio-text"><strong>Delete Folder Only</strong><span>Removes folder name, keeps items.</span></div>
                            </label>
                            <label className="add-galley-of-sk-college-radio-label">
                                <input type="radio" name="delType" checked={deleteType === 'content'} onChange={() => setDeleteType('content')} />
                                <div className="radio-text"><strong>Delete Folder & Contents</strong><span>Permanently deletes folder and all images inside.</span></div>
                            </label>
                        </div>
                    )}
                </div>
                <div className="add-galley-of-sk-college-modal-footer">
                    <button className="add-galley-of-sk-college-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className={`add-galley-of-sk-college-btn-save ${activeTab === 'delete' ? 'danger' : ''}`} onClick={handleExecute}>{activeTab === 'rename' ? 'Save Changes' : 'Delete Now'}</button>
                </div>
            </div>
        </div>
    );
};

// --- ADD/EDIT ITEM MODAL ---
const GalleryFormModal = ({ isOpen, onClose, data, mode, onSave, existingFolders }) => {
    const [formData, setFormData] = useState({ folderName: '', description: '', isActive: true });
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState('');
    const [isNewFolder, setIsNewFolder] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            if (data) {
                setFormData({
                    folderName: data.folderName,
                    description: data.description,
                    isActive: data.isActive,
                });
                setPreview(data.image ? `${API_BASE_URL}${data.image}` : '');
                setIsNewFolder(!existingFolders.includes(data.folderName));
                setImageFile(null);
            } else {
                setFormData({ folderName: '', description: '', isActive: true });
                setPreview('');
                setImageFile(null);
                setIsNewFolder(false);
            }
        }
    }, [data, isOpen, existingFolders]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveMedia = (e) => {
        e.stopPropagation();
        setImageFile(null);
        setPreview('');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = () => {
        if (mode === 'add' && !imageFile) return Swal.fire('Error', 'Please upload a cover image', 'error');
        if (!formData.folderName) return Swal.fire('Error', 'Please enter or select a folder name', 'error');

        const submissionData = new FormData();
        submissionData.append('folderName', formData.folderName);
        submissionData.append('description', formData.description || '');
        submissionData.append('isActive', formData.isActive.toString());

        if (imageFile) {
            submissionData.append('image', imageFile);
        }

        onSave(submissionData);
    };

    if (!isOpen) return null;

    return (
        <div className="add-galley-of-sk-college-modal-overlay">
            <div className="add-galley-of-sk-college-modal-content" onClick={e => e.stopPropagation()}>
                <div className="add-galley-of-sk-college-modal-header">
                    <h2>{data ? 'Edit Gallery Item' : 'Add Gallery Item'}</h2>
                    <button className="add-galley-of-sk-college-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="add-galley-of-sk-college-modal-body single-column">
                    <div className="add-galley-of-sk-college-form-group">
                        <div className="add-galley-of-sk-college-label-row">
                            <label>Folder Name</label>
                            {!data && (
                                <span className="add-galley-of-sk-college-toggle-link" onClick={() => { setIsNewFolder(!isNewFolder); setFormData({...formData, folderName: ''}) }}>
                                    {isNewFolder ? 'Select Existing' : '+ Add New Folder'}
                                </span>
                            )}
                        </div>
                        {isNewFolder || (data && !existingFolders.includes(formData.folderName)) ? (
                            <input type="text" name="folderName" value={formData.folderName} onChange={handleChange} className="add-galley-of-sk-college-input" placeholder="Enter new folder name" />
                        ) : (
                            <select name="folderName" value={formData.folderName} onChange={handleChange} className="add-galley-of-sk-college-select-input">
                                <option value="">-- Select Folder --</option>
                                {existingFolders.filter(f => f).map((f, i) => <option key={i} value={f}>{f}</option>)}
                            </select>
                        )}
                    </div>
                    <div className="add-galley-of-sk-college-form-group">
                        <label>Cover Image</label>
                        <div className="add-galley-of-sk-college-preview-box large" onClick={() => fileInputRef.current.click()}>
                            {preview ? (
                                <div className="add-galley-of-sk-college-image-container">
                                    <img src={preview} alt="Cover" />
                                    <div className="add-galley-of-sk-college-overlay">
                                        <button className="add-galley-of-sk-college-mini-btn" onClick={(e) => {e.stopPropagation(); fileInputRef.current.click();}}><FaExchangeAlt /></button>
                                        <button className="add-galley-of-sk-college-mini-btn remove" onClick={handleRemoveMedia}><FaTrashAlt /></button>
                                    </div>
                                </div>
                            ) : (
                                <div className="add-galley-of-sk-college-upload-placeholder"><FaUpload size={28}/><span>Click to Upload Image</span></div>
                            )}
                            <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} accept="image/*" />
                        </div>
                    </div>
                    <div className="add-galley-of-sk-college-form-group">
                        <label>Description</label>
                        <textarea name="description" value={formData.description} onChange={handleChange} className="add-galley-of-sk-college-textarea" placeholder="Enter description..." rows="3" />
                    </div>
                </div>
                <div className="add-galley-of-sk-college-modal-footer">
                    <button className="add-galley-of-sk-college-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="add-galley-of-sk-college-btn-save" onClick={handleSubmit}><FaSave style={{marginRight: '5px'}}/> Save</button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const AddGallery = () => {
    const [galleryData, setGalleryData] = useState([]); 
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedItem, setSelectedItem] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    // --- FETCH USING USECALLBACK TO FIX ESLINT WARNING ---
    const fetchGalleryData = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/gallery');
            const data = response.data;
            if (Array.isArray(data)) setGalleryData(data);
            else setGalleryData([]);
        } catch (error) {
            console.error("Error fetching gallery:", error);
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Failed to load gallery data', 'error');
            }
            setGalleryData([]);
        }
    }, []);

    useEffect(() => { 
        fetchGalleryData(); 
    }, [fetchGalleryData]); // Dependency added safely here

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    
    const uniqueFolders = useMemo(() => [...new Set(galleryData.map(d => d.folderName).filter(Boolean))], [galleryData]);

    const filteredData = useMemo(() => {
        return galleryData.filter(item => {
            const term = searchTerm.toLowerCase();
            const fName = item.folderName ? item.folderName.toLowerCase() : 'n/a';
            return (fName.includes(term));
        });
    }, [galleryData, searchTerm]);

    const totalResults = filteredData.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startResult = totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalResults);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    const handleSaveItem = async (formData) => {
        try {
            if (modalMode === 'edit') {
                await axiosInstance.put(`/api/gallery/${selectedItem._id}`, formData);
            } else {
                await axiosInstance.post('/api/gallery', formData);
            }

            await fetchGalleryData();
            Swal.fire(modalMode === 'edit' ? 'Updated!' : 'Added!', 'Operation successful.', 'success');
            setIsItemModalOpen(false);
        } catch (error) {
            console.error("Save Error:", error);
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Server error occurred', 'error');
            }
        }
    };

    const handleDeleteItem = (id) => {
        Swal.fire({ title: 'Delete Item?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e11d48' }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/gallery/${id}`);
                    await fetchGalleryData();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    if (error.response?.status !== 401) {
                        Swal.fire('Error', 'Could not delete item.', 'error');
                    }
                }
            }
        });
    };

    const toggleActive = async (id, currentStatus) => {
        try {
            await axiosInstance.put(`/api/gallery/${id}`, { isActive: !currentStatus });
            await fetchGalleryData();
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Could not update status.', 'error');
            }
        }
    };

    const openItemModal = (mode, item = null) => {
        setModalMode(mode);
        setSelectedItem(item);
        setIsItemModalOpen(true);
    };

    const performFolderAction = async (payload) => {
        try {
            const response = await axiosInstance.put('/api/gallery/folder', payload);
            await fetchGalleryData();
            Swal.fire('Success', response.data.message, 'success');
            setIsToolsModalOpen(false);
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Action failed', 'error');
            }
        }
    };

    return (
        <div className="add-galley-of-sk-college-wrapper">
            <div className="add-galley-of-sk-college-container">
                <GalleryFormModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} data={selectedItem} mode={modalMode} onSave={handleSaveItem} existingFolders={uniqueFolders} />
                <FolderToolsModal isOpen={isToolsModalOpen} onClose={() => setIsToolsModalOpen(false)} folders={uniqueFolders} onRename={(oldName, newName) => performFolderAction({ action: 'rename', oldName, newName })} onDelete={(folderName, deleteType) => performFolderAction({ action: 'delete', folderName, deleteType })} />

                <div className="add-galley-of-sk-college-header">
                    <div className="add-galley-of-sk-college-header-title"><h1>Gallery Management</h1></div>
                    <button className="add-galley-of-sk-college-add-btn" onClick={() => openItemModal('add')}><FaPlus /> Add Gallery</button>
                </div>

                <div className="add-galley-of-sk-college-controls-row">
                    <div className="add-galley-of-sk-college-search-wrapper">
                        <FaSearch className="add-galley-of-sk-college-search-icon" />
                        <input type="text" placeholder="Search folder name..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="add-galley-of-sk-college-search-input" />
                    </div>
                    <div className="add-galley-of-sk-college-tools-wrapper">
                        <button className="add-galley-of-sk-college-tools-btn" onClick={() => setIsToolsModalOpen(true)}><FaCog /> Edit / Delete Folder</button>
                    </div>
                    <div className="add-galley-of-sk-college-filter-group">
                        <label>Show:</label>
                        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="add-galley-of-sk-college-select">
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                    </div>
                </div>

                <div className="add-galley-of-sk-college-table-card">
                    <div className="add-galley-of-sk-college-table-wrapper">
                        <table className="add-galley-of-sk-college-table">
                            <thead>
                                <tr>
                                    <th className="add-galley-of-sk-college-col-sno">S.No</th>
                                    <th className="add-galley-of-sk-college-col-name">Folder Name</th>
                                    <th className="add-galley-of-sk-college-col-image">Image</th>
                                    <th className="add-galley-of-sk-college-col-desc">Description</th>
                                    <th className="add-galley-of-sk-college-col-created">Created At</th>
                                    <th className="add-galley-of-sk-college-col-updated">Updated At</th>
                                    <th className="add-galley-of-sk-college-col-active">Active</th>
                                    <th className="add-galley-of-sk-college-col-actions">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((item, index) => (
                                    <React.Fragment key={item._id}>
                                        <tr className="add-galley-of-sk-college-row">
                                            <td className="add-galley-of-sk-college-col-sno">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td className="add-galley-of-sk-college-col-name">
                                                <div className="add-galley-of-sk-college-name-wrapper">
                                                    {item.folderName ? <><FaFolder className="folder-icon"/> {item.folderName}</> : <span>N/A</span>}
                                                    <button className="add-galley-of-sk-college-mobile-toggle-btn" onClick={() => toggleRow(item._id)}>{expandedRow === item._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                                                </div>
                                            </td>
                                            <td className="add-galley-of-sk-college-col-image">
                                                <div className="add-galley-of-sk-college-table-thumb">
                                                    <img src={`${API_BASE_URL}${item.image}`} alt="Thumb" onError={(e) => e.target.src = 'https://via.placeholder.com/50'} />
                                                </div>
                                            </td>
                                            <td className="add-galley-of-sk-college-col-desc"><div className="add-galley-of-sk-college-truncate">{item.description}</div></td>
                                            <td className="add-galley-of-sk-college-col-created">{formatDate(item.createdAt)}</td>
                                            <td className="add-galley-of-sk-college-col-updated">{formatDate(item.updatedAt)}</td>
                                            <td className="add-galley-of-sk-college-col-active">
                                                <div className="add-galley-of-sk-college-switch" onClick={() => toggleActive(item._id, item.isActive)}>
                                                    <div className={`add-galley-of-sk-college-switch-handle ${item.isActive ? 'active' : ''}`}></div>
                                                </div>
                                            </td>
                                            <td className="add-galley-of-sk-college-col-actions">
                                                <div className="add-galley-of-sk-college-action-icons">
                                                    <button className="add-galley-of-sk-college-icon-btn edit" onClick={() => openItemModal('edit', item)}><FaEdit /></button>
                                                    <button className="add-galley-of-sk-college-icon-btn delete" onClick={() => handleDeleteItem(item._id)}><FaTrashAlt /></button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRow === item._id && (
                                            <tr className="add-galley-of-sk-college-details-row">
                                                <td colSpan="8">
                                                    <div className="add-galley-of-sk-college-details-content">
                                                         <div className="add-galley-of-sk-college-details-grid column-wise">
                                                            <div className="add-galley-of-sk-college-detail-card dc-image">
                                                                <div className="add-galley-of-sk-college-detail-label">Image</div>
                                                                <div className="add-galley-of-sk-college-detail-thumb">
                                                                    <img src={`${API_BASE_URL}${item.image}`} alt="Cover" />
                                                                </div>
                                                            </div>
                                                            <div className="add-galley-of-sk-college-detail-card dc-desc">
                                                                <div className="add-galley-of-sk-college-detail-label">Description</div>
                                                                <div className="add-galley-of-sk-college-detail-text">{item.description}</div>
                                                            </div>
                                                            <div className="add-galley-of-sk-college-detail-card dc-actions">
                                                                <div className="add-galley-of-sk-college-detail-label">Actions</div>
                                                                <div className="add-galley-of-sk-college-action-icons">
                                                                    <button className="add-galley-of-sk-college-icon-btn edit" onClick={() => openItemModal('edit', item)}><FaEdit /></button>
                                                                    <button className="add-galley-of-sk-college-icon-btn delete" onClick={() => handleDeleteItem(item._id)}><FaTrashAlt /></button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="add-galley-of-sk-college-pagination-container">
                    <div className="add-galley-of-sk-college-pagination-info">Showing {startResult} to {endResult} of {totalResults} results</div>
                    <div className="add-galley-of-sk-college-pagination-controls">
                        <button className="add-galley-of-sk-college-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        <span className="add-galley-of-sk-college-page-count">Page {currentPage} of {totalPages || 1}</span>
                        <button className="add-galley-of-sk-college-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddGallery;